import { makeAutoObservable, runInAction } from "mobx";

import {

  countDueReminders,

  getNextReminderDelayMs,

  isNoteReminderDue,

  parseRemindAtValue,

  SNOOZE_MS,

} from "../utils/notesReminderUtils";

import {

  createNoteId,

  readNotesList,

  readNotesWidgetEnabled,

  readNotesWidgetOpen,

  readNotesWidgetPosition,

  readNotesWidgetSize,

  writeNotesList,

  writeNotesWidgetEnabled,

  writeNotesWidgetOpen,

  writeNotesWidgetPosition,

  writeNotesWidgetSize,

} from "../utils/notesWidgetStorage";



const MAX_TIMEOUT_MS = 2147483647;



class NotesWidgetStore {

  enabled = readNotesWidgetEnabled();

  open = readNotesWidgetOpen();

  position = readNotesWidgetPosition();

  panelSize = readNotesWidgetSize();

  notes = readNotesList();

  /** @type {string[]} note ids with visible reminder toasts */

  activeReminderToasts = [];

  highlightedNoteId = null;

  _reminderTimeoutId = null;

  _reminderEngineStarted = false;



  constructor() {

    makeAutoObservable(this, {

      _reminderTimeoutId: false,

      _reminderEngineStarted: false,

    });

  }



  get dueReminderCount() {

    return countDueReminders(this.notes);

  }



  setEnabled = (value) => {

    runInAction(() => {

      this.enabled = value;

      writeNotesWidgetEnabled(value);

      if (!value) {

        this.open = false;

        writeNotesWidgetOpen(false);

        this.activeReminderToasts = [];

        this.stopReminderEngine();

      } else {

        this.startReminderEngine();

      }

    });

  };



  setOpen = (value) => {

    if (!this.enabled) {

      return;

    }

    runInAction(() => {

      this.open = value;

      writeNotesWidgetOpen(value);

    });

  };



  toggleOpen = () => {

    this.setOpen(!this.open);

  };



  setPosition = (position) => {

    runInAction(() => {

      this.position = { ...position };

      writeNotesWidgetPosition(this.position);

    });

  };



  setPanelSize = (size) => {

    runInAction(() => {

      this.panelSize = { ...size };

      writeNotesWidgetSize(this.panelSize);

    });

  };



  addNote = () => {

    runInAction(() => {

      this.notes = [

        ...this.notes,

        {

          id: createNoteId(),

          text: "",

          remindAt: null,

          dismissedForRemindAt: null,

          reminderEnabled: false,

        },

      ];

      writeNotesList(this.notes);

      this.rescheduleReminders();

    });

  };



  updateNoteText = (id, text) => {

    runInAction(() => {

      this.notes = this.notes.map((n) =>

        n.id === id ? { ...n, text } : n,

      );

      writeNotesList(this.notes);

    });

  };



  updateNoteRemindAt = (id, inputValue) => {

    runInAction(() => {

      const remindAt = inputValue ? parseRemindAtValue(inputValue) : null;

      this.notes = this.notes.map((n) => {

        if (n.id !== id) {

          return n;

        }

        return {

          ...n,

          remindAt,

          dismissedForRemindAt: null,

        };

      });

      writeNotesList(this.notes);

      this.syncReminderToasts();

      this.rescheduleReminders();

    });

  };



  updateNoteReminderEnabled = (id, enabled) => {

    runInAction(() => {

      this.notes = this.notes.map((n) => {

        if (n.id !== id) {

          return n;

        }

        if (!enabled) {

          return {

            ...n,

            reminderEnabled: false,

            remindAt: null,

            dismissedForRemindAt: null,

          };

        }

        return { ...n, reminderEnabled: true };

      });

      writeNotesList(this.notes);

      this.syncReminderToasts();

      this.rescheduleReminders();

    });

  };



  removeNote = (id) => {

    runInAction(() => {

      this.notes = this.notes.filter((n) => n.id !== id);

      this.activeReminderToasts = this.activeReminderToasts.filter(

        (toastId) => toastId !== id,

      );

      if (this.highlightedNoteId === id) {

        this.highlightedNoteId = null;

      }

      writeNotesList(this.notes);

      this.rescheduleReminders();

    });

  };



  dismissReminder = (noteId) => {

    runInAction(() => {

      const note = this.notes.find((n) => n.id === noteId);

      if (note?.remindAt != null) {

        this.notes = this.notes.map((n) =>

          n.id === noteId

            ? { ...n, dismissedForRemindAt: n.remindAt }

            : n,

        );

        writeNotesList(this.notes);

      }

      this.activeReminderToasts = this.activeReminderToasts.filter(

        (id) => id !== noteId,

      );

      this.rescheduleReminders();

    });

  };



  snoozeReminder = (noteId) => {

    runInAction(() => {

      const nextAt = Date.now() + SNOOZE_MS;

      this.notes = this.notes.map((n) =>

        n.id === noteId

          ? {

              ...n,

              remindAt: nextAt,

              dismissedForRemindAt: null,

            }

          : n,

      );

      writeNotesList(this.notes);

      this.activeReminderToasts = this.activeReminderToasts.filter(

        (id) => id !== noteId,

      );

      this.rescheduleReminders();

    });

  };



  openReminderNote = (noteId) => {

    runInAction(() => {

      const note = this.notes.find((n) => n.id === noteId);

      if (note?.remindAt != null) {

        this.notes = this.notes.map((n) =>

          n.id === noteId

            ? { ...n, dismissedForRemindAt: n.remindAt }

            : n,

        );

        writeNotesList(this.notes);

      }

      this.activeReminderToasts = this.activeReminderToasts.filter(

        (id) => id !== noteId,

      );

      this.highlightedNoteId = noteId;

      this.open = true;

      writeNotesWidgetOpen(true);

      this.rescheduleReminders();

    });

  };



  clearHighlight = () => {

    runInAction(() => {

      this.highlightedNoteId = null;

    });

  };



  syncReminderToasts = () => {

    const dueIds = this.notes

      .filter((n) => isNoteReminderDue(n))

      .map((n) => n.id);

    const merged = [...this.activeReminderToasts];

    for (const id of dueIds) {

      if (!merged.includes(id)) {

        merged.push(id);

      }

    }

    this.activeReminderToasts = merged.filter((id) =>

      this.notes.some((n) => n.id === id && isNoteReminderDue(n)),

    );

  };



  checkDueReminders = () => {

    if (!this.enabled) {

      return;

    }

    runInAction(() => {

      this.syncReminderToasts();

      this.rescheduleReminders();

    });

  };



  rescheduleReminders = () => {

    if (this._reminderTimeoutId != null) {

      clearTimeout(this._reminderTimeoutId);

      this._reminderTimeoutId = null;

    }

    if (!this.enabled) {

      return;

    }

    const delay = getNextReminderDelayMs(this.notes);

    if (delay == null) {

      return;

    }

    const ms = Math.min(delay, MAX_TIMEOUT_MS);

    this._reminderTimeoutId = setTimeout(() => {

      this.checkDueReminders();

    }, ms);

  };



  stopReminderEngine = () => {

    if (this._reminderTimeoutId != null) {

      clearTimeout(this._reminderTimeoutId);

      this._reminderTimeoutId = null;

    }

  };



  startReminderEngine = () => {

    if (this._reminderEngineStarted) {

      this.checkDueReminders();

      return;

    }

    this._reminderEngineStarted = true;

    this.checkDueReminders();

    if (typeof document !== "undefined") {

      document.addEventListener("visibilitychange", this._onVisibilityChange);

    }

  };



  _onVisibilityChange = () => {

    if (document.visibilityState === "visible") {

      this.checkDueReminders();

    }

  };

}



const notesWidgetStore = new NotesWidgetStore();

export default notesWidgetStore;


